import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * useFormAttention
 * - 安靜模式 quiet-mode：控制 notEmpty 類提示在填寫過程中的顯示
 * - 下一步/送出：未通過時關閉 quiet、捲到第一個錯誤並聚焦
 * - 題組完成且無可見錯誤時，自動移除 attention 紅框
 * - step 切換成功後，自動回到 quiet 並捲到該 step 頂部
 */
export const useFormAttention = (
  formId = 'form-inputs',
  nextButtonId = 'nextButton',
  submitButtonId = 'submit-btn',
  options = {}
) => {
  const { preview = false, curStep = 1, num_se = 1 } = options;
  const skipNextValidation = preview && curStep !== num_se;
  const [isQuietMode, setIsQuietMode] = useState(true);
  const formRef = useRef(null);
  const lastStepRef = useRef(null);
  const rafIdRef = useRef(null);

  // 取得目前顯示的 step
  const getActiveStep = useCallback(() => {
    return document.querySelector('.js-step.js-step-active');
  }, []);

  // 可見性判斷
  const isVisible = (el) => {
    if (!el) return false;
    const style = getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
    if (!el.offsetParent && style.position !== 'fixed') return false;
    return true;
  };

  // 捲到題目並嘗試聚焦第一個可互動欄位
  const scrollToQuest = (quest) => {
    if (!quest) return;
    // 建議搭配 CSS: .quest, .quest_list { scroll-margin-top: 80px; }
    quest.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const focusable = quest.querySelector(
      'input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled])'
    );
    if (focusable) {
      setTimeout(() => focusable.focus?.(), 200);
    }
  };

  // ===== 驗證輔助 =====
  const hasQuestCallbackError = useCallback((quest) => {
    if (!quest) return false;
    const msgs = quest.querySelectorAll('.fv-plugins-message-container [data-validator="callback"]');
    for (const m of msgs) {
      const txt = (m.textContent || '').trim();
      if (txt.length > 0 && isVisible(m)) return true;
    }
    return false;
  }, []);

  const hasVisibleMsg = useCallback((quest) => {
    if (!quest) return false;
    const msgs = quest.querySelectorAll('.fv-plugins-message-container [data-validator]');
    for (const m of msgs) {
      const txt = (m.textContent || '').trim();
      if (txt.length > 0 && isVisible(m)) return true;
    }
    return false;
  }, []);

  const isRowVisuallyValid = useCallback((quest) => {
    const grp = quest.querySelector('.form-group.required, .form-group.text.required');
    return !!(grp && grp.classList.contains('fv-plugins-bootstrap5-row-valid'));
  }, []);

  // 依 name 群組檢查是否完成：
  // - radio/checkbox：同名至少一個 checked
  // - 文字/下拉/多行：值非空
  const isQuestAllAnswered = useCallback((quest) => {
    const controls = quest.querySelectorAll('input, select, textarea');
    if (!controls.length) return true;

    const groups = new Map();
    let sawAny = false;

    controls.forEach(el => {
      if (el.disabled) return;
      if (el.tagName === 'INPUT' && (el.type || '').toLowerCase() === 'hidden') return;

      sawAny = true;
      const name = el.name || `__anon_${groups.size}_${Math.random()}`;
      const type = (el.type || '').toLowerCase();
      const isChoice = el.tagName === 'INPUT' && (type === 'radio' || type === 'checkbox');

      if (!groups.has(name)) groups.set(name, { kind: isChoice ? 'choice' : 'value', ok: false });
      const g = groups.get(name);

      if (g.kind === 'choice' && isChoice) {
        if (el.checked) g.ok = true;
      } else if (g.kind === 'value' && !isChoice) {
        const val = el.value ?? '';
        if (String(val).trim() !== '') g.ok = true;
      }
    });

    if (!sawAny) return true;
    for (const [, g] of groups) if (!g.ok) return false;
    return true;
  }, []);

  const isQuestFilled = useCallback((quest) => {
    if (!quest) return false;
    const requiredGroup = quest.querySelector('.form-group.required, .form-group.text.required');
    if (!requiredGroup) return true;
    return isQuestAllAnswered(quest);
  }, [isQuestAllAnswered]);

  // 放寬後的清紅框條件：已完成 + 無可見錯誤；或 FV 行視覺 valid
  const tryClearAttention = useCallback((quest) => {
    if (!quest) return;

    const run = () => {
      const filled = isQuestFilled(quest);
      const noCb   = !hasQuestCallbackError(quest);
      const noMsg  = !hasVisibleMsg(quest);
      const rowOk  = isRowVisuallyValid(quest);
      const hasInvalidRow = !!quest.querySelector('.fv-plugins-bootstrap5-row-invalid');

      const ok = noCb && (rowOk || (filled && noMsg && !hasInvalidRow));
      if (ok) quest.classList.remove('attention');
    };

    run();
    setTimeout(() => {
      run();
      requestAnimationFrame(run);
    }, 0);
  }, [isQuestFilled, hasQuestCallbackError, hasVisibleMsg, isRowVisuallyValid]);

  // rAF 排程掃描當前 step 的 attention 題
  const scheduleReconcile = useCallback(() => {
    if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
    rafIdRef.current = requestAnimationFrame(() => {
      rafIdRef.current = null;
      const active = getActiveStep();
      if (!active) return;
      active.querySelectorAll('.quest.attention').forEach(tryClearAttention);
    });
  }, [getActiveStep, tryClearAttention]);

  // 取得第一個需要注意的位置
  const getFirstProblemQuest = useCallback(() => {
    const active = getActiveStep();
    if (!active) return null;

    const q1 = active.querySelector('.quest.attention');
    if (q1) return q1;

    const msg = active.querySelector('.fv-plugins-message-container [data-validator]');
    if (msg) return msg.closest('.quest');

    return null;
  }, [getActiveStep]);

  // 標出當前 step 缺漏
  const markMissingInActiveStep = useCallback(() => {
    const active = getActiveStep();
    if (!active) return { hasMissing: false };
    let hasMissing = false;

    active.querySelectorAll('.quest').forEach(q => {
      const invalidRow = q.querySelector('.fv-plugins-bootstrap5-row-invalid');
      if (!isQuestFilled(q) || hasQuestCallbackError(q) || hasVisibleMsg(q) || invalidRow) {
        q.classList.add('attention');
        hasMissing = true;
      } else {
        tryClearAttention(q);
      }
    });

    return { hasMissing };
  }, [getActiveStep, isQuestFilled, hasQuestCallbackError, hasVisibleMsg, tryClearAttention]);

  const hasActiveCallbackError = useCallback(() => {
    const active = getActiveStep();
    if (!active) return false;
    const msgs = active.querySelectorAll('.fv-plugins-message-container [data-validator="callback"]');
    for (const m of msgs) {
      const txt = (m.textContent || '').trim();
      if (txt.length > 0 && isVisible(m)) return true;
    }
    return false;
  }, [getActiveStep]);

  // ===== 初始化：進來先安靜；只有真的換 step 才把 quiet-mode 打開並捲到頂 =====
  useEffect(() => {
    const form = document.getElementById(formId);
    formRef.current = form;
    if (!form) return;

    form.classList.add('quiet-mode');
    setIsQuietMode(true);
    lastStepRef.current = getActiveStep();

    const scrollToStepTop = () => {
      const active = getActiveStep();
      if (!active) return;
      const target = active.querySelector('.quest_list') || active.querySelector('.quest') || active;
      setTimeout(() => target.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
    };

    const reQuietIfStepChanged = () => {
      const cur = getActiveStep();
      if (cur && cur !== lastStepRef.current) {
        lastStepRef.current = cur;
        setIsQuietMode(true);
        form.classList.add('quiet-mode');
        scrollToStepTop();
        scheduleReconcile();
      }
    };

    document.addEventListener('transitionend', reQuietIfStepChanged, true);
    document.addEventListener('animationend', reQuietIfStepChanged, true);

    return () => {
      document.removeEventListener('transitionend', reQuietIfStepChanged, true);
      document.removeEventListener('animationend', reQuietIfStepChanged, true);
    };
  }, [formId, getActiveStep, scheduleReconcile]);

  // ===== 綁定 下一步 / 送出：驗證未過關閉 quiet、捲到第一個錯誤並聚焦 =====
  useEffect(() => {
    const form = formRef.current;
    if (!form) return;
    const nextBtn = document.getElementById(nextButtonId);
    const submitBtn = document.getElementById(submitButtonId);

    const onNextClick = (e) => {
      if (skipNextValidation) {
        return;
      }
      setIsQuietMode(false);
      form.classList.remove('quiet-mode');

      const { hasMissing } = markMissingInActiveStep();
      const hasCb = hasActiveCallbackError();

      if (hasMissing || hasCb) {
        e.preventDefault?.();
        e.stopImmediatePropagation?.();

        requestAnimationFrame(() => {
          const targetQuest = getFirstProblemQuest();
          if (targetQuest) scrollToQuest(targetQuest);
        });

        scheduleReconcile();
        return;
      }
      // 通過：放行，真正換 step 後由 reQuietIfStepChanged 開回 quiet 並捲到頂
    };

    const hasBlockingErrors = (scope) => {
      const root = scope || document;
      const msgs = root.querySelectorAll('.fv-plugins-message-container [data-validator]');
      for (const m of msgs) {
        const txt = (m.textContent || '').trim();
        if (txt.length > 0 && isVisible(m)) return true;
      }
      if (root.querySelector('.fv-plugins-bootstrap5-row-invalid')) return true;
      if (root.querySelector('.quest.attention')) return true;
      return false;
    };

    const onSubmitClick = () => {
      setIsQuietMode(false);
      form.classList.remove('quiet-mode');
    };

    const onFormSubmit = (e) => {
      setIsQuietMode(false);
      form.classList.remove('quiet-mode');

      setTimeout(() => {
        const scope = getActiveStep() || document;
        if (hasBlockingErrors(scope)) {
          requestAnimationFrame(() => {
            const targetQuest = getFirstProblemQuest();
            if (targetQuest) scrollToQuest(targetQuest);
          });
        }
        requestAnimationFrame(() => {
          const scope2 = getActiveStep() || document;
          if (hasBlockingErrors(scope2)) {
            const targetQuest2 = getFirstProblemQuest();
            if (targetQuest2) scrollToQuest(targetQuest2);
          }
        });
      }, 0);
    };

    nextBtn?.addEventListener('click', onNextClick, { capture: true });
    submitBtn?.addEventListener('click', onSubmitClick, { capture: true });
    form.addEventListener('submit', onFormSubmit, { capture: true });

    return () => {
      nextBtn?.removeEventListener('click', onNextClick, { capture: true });
      submitBtn?.removeEventListener('click', onSubmitClick, { capture: true });
      form.removeEventListener('submit', onFormSubmit, { capture: true });
    };
  }, [
    nextButtonId,
    submitButtonId,
    skipNextValidation,
    getActiveStep,
    markMissingInActiveStep,
    hasActiveCallbackError,
    scheduleReconcile,
    getFirstProblemQuest
  ]);

  // ===== 互動就嘗試清紅框（不變動 quiet 狀態）=====
  const handleInputChange = useCallback((e) => {
    const quest = e.target.closest?.('.quest');
    if (!quest) return;
    tryClearAttention(quest);
    scheduleReconcile();
  }, [tryClearAttention, scheduleReconcile]);

  useEffect(() => {
    const form = formRef.current;
    if (!form) return;

    form.addEventListener('input', handleInputChange, true);
    form.addEventListener('change', handleInputChange, true);

    // 監控驗證訊息：quiet 下隱藏 notEmpty；callback 出現就標 attention
    const mo = new MutationObserver(muts => {
      let touched = false;
      for (const m of muts) {
        // 限 form 範圍
        if (m.target && form !== m.target && !form.contains(m.target)) continue;

        m.addedNodes.forEach(node => {
          if (!(node instanceof HTMLElement)) return;
          const el = node.matches?.('[data-validator]') ? node : node.querySelector?.('[data-validator]');
          if (!el) return;
          const quest = el.closest('.quest');
          if (!quest) return;

          const v = el.getAttribute('data-validator');
          if (v === 'callback') {
            el.style.display = '';        // 立刻顯示 callback
            quest.classList.add('attention');
            touched = true;
          } else if (v === 'notEmpty' && isQuietMode) {
            // 保留 DOM，但在 quiet 模式下暫時隱藏
            el.style.display = 'none';
            touched = true;
          }
        });

        if (m.removedNodes.length) touched = true;
      }
      if (touched) scheduleReconcile();
    });
    mo.observe(form, { childList: true, subtree: true });

    // 初始對齊
    scheduleReconcile();

    return () => {
      form.removeEventListener('input', handleInputChange, true);
      form.removeEventListener('change', handleInputChange, true);
      mo.disconnect();
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
    };
  }, [handleInputChange, isQuietMode, scheduleReconcile]);

  // 同步 quiet-mode class
  useEffect(() => {
    if (!formRef.current) return;
    if (isQuietMode) formRef.current.classList.add('quiet-mode');
    else formRef.current.classList.remove('quiet-mode');
  }, [isQuietMode]);

  // 提供給外層（若你要手動在 onClick 中呼叫）
  const validateBeforeNext = useCallback((e) => {
    if (!formRef.current) return true;
    if (skipNextValidation) return true;

    setIsQuietMode(false);
    formRef.current.classList.remove('quiet-mode');

    const { hasMissing } = markMissingInActiveStep();
    const hasCb = hasActiveCallbackError();
    if (hasMissing || hasCb) {
      e?.preventDefault?.();
      e?.stopImmediatePropagation?.();
      requestAnimationFrame(() => {
        const targetQuest = getFirstProblemQuest();
        if (targetQuest) scrollToQuest(targetQuest);
      });
      scheduleReconcile();
      return false;
    }
    return true;
  }, [skipNextValidation, markMissingInActiveStep, hasActiveCallbackError, scheduleReconcile, getFirstProblemQuest]);

  return {
    isQuietMode,
    validateBeforeNext,
    scheduleReconcile,
  };
};
